/**
 * IndieChatRightPanel
 * Right 256px pane: Actions tab (numbered priority cards) · Artifacts tab
 * Pixel-faithful port from Figma export (fusion-starter-6c1/client/pages/Index.tsx)
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download, MoreVertical, RefreshCw, Loader2, FileText, FileSpreadsheet, Trash2, X, Pencil, Check, Copy, Share2 } from 'lucide-react';
import {
  RightTab,
  ActionCard,
  badgeStyles,
  defaultActionCards,
  ArtifactBarChartIcon,
  ArtifactListIcon,
  ArtifactCartIcon,
} from './indie-chat.types';
import type { OrbitArtifact } from '../../hooks/useOrbitArtifacts';
import GlobalTip from '../../providers/GlobalTip';
import { GeneratedFilePreview, IndieGeneratedFilesTab, downloadGeneratedFile, isBuilding, useLiveGeneratedFile } from './IndieGeneratedFiles';

interface Props {
  activeTab: RightTab;
  onTabChange: (t: RightTab) => void;
  actionCards?: ActionCard[];
  weekLabel?: string;
  onActionSelect?: (text: string) => void;
  onRefresh?: () => Promise<void>;
  refreshing?: boolean;
  artifacts?: OrbitArtifact[];
  artifactsLoading?: boolean;
  onArtifactUpdate?: (artifactId: number, input: { title?: string; content_markdown?: string; artifact_type?: string }) => Promise<OrbitArtifact | undefined>;
  onArtifactDownload?: (artifact: OrbitArtifact) => void;
  onArtifactDelete?: (artifact: OrbitArtifact) => void;
  onArtifactSelect?: (artifact: OrbitArtifact) => void;
  /** Files tab: generated files for this client, current conversation first */
  clientId?: number;
  conversationId?: string;
}

function artifactSectionLabel(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();
  if (!Number.isFinite(created.getTime())) return 'Saved items';
  if (created.getFullYear() === now.getFullYear() && created.getMonth() === now.getMonth()) {
    return 'This month';
  }
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (created.getFullYear() === lastMonth.getFullYear() && created.getMonth() === lastMonth.getMonth()) {
    return 'Last month';
  }
  return created.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function artifactSubtitle(artifact: OrbitArtifact): string {
  const created = new Date(artifact.created_at);
  const dateLabel = Number.isFinite(created.getTime())
    ? created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : 'Recently';
  const typeLabel = artifact.artifact_type.replace(/_/g, ' ');
  return `Saved ${dateLabel} · ${typeLabel}`;
}

const FILE_FORMAT_LABEL: Record<string, string> = { xlsx: 'Excel', pdf: 'PDF', pptx: 'Slides', csv: 'CSV' };

type ArtifactFile = NonNullable<OrbitArtifact['file']>;

function fileStatusLabel(file: ArtifactFile): string {
  const format = FILE_FORMAT_LABEL[file.file_format] ?? file.file_format.toUpperCase();
  if (isBuilding(file.status)) return `${format} · Building…`;
  if (file.status === 'error') return `${format} · ${file.error_message || 'Build failed'}`;
  return `${format} · ${file.filename}`;
}

function artifactVisual(artifact: OrbitArtifact): { iconBg: string; icon: React.ReactNode } {
  if (artifact.file) {
    return artifact.file.file_format === 'xlsx' || artifact.file.file_format === 'csv'
      ? { iconBg: '#ECFDF3', icon: <FileSpreadsheet className="w-3.5 h-3.5 text-[#079455]" /> }
      : { iconBg: '#F4EBFF', icon: <FileText className="w-3.5 h-3.5 text-[#6941C6]" /> };
  }
  const type = artifact.artifact_type.toLowerCase();
  if (type.includes('action')) {
    return { iconBg: '#F9F5FF', icon: <ArtifactListIcon /> };
  }
  if (type.includes('buy') || type.includes('report')) {
    return { iconBg: '#FFFAEB', icon: <ArtifactCartIcon /> };
  }
  return { iconBg: '#FEF3F2', icon: <ArtifactBarChartIcon /> };
}

const ActionsTabIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1.625 9.20833L2.70833 10.2917L4.875 8.125" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.625 3.79159L2.70833 4.87492L4.875 2.70825" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.04163 3.25H11.375" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.04163 6.5H11.375" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.04163 9.75H11.375" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArtifactsTabIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <g clipPath="url(#art)">
      <path d="M8.12496 1.08325H3.24996C2.96264 1.08325 2.68709 1.19739 2.48393 1.40055C2.28076 1.60372 2.16663 1.87927 2.16663 2.16659V10.8333C2.16663 11.1206 2.28076 11.3961 2.48393 11.5993C2.68709 11.8024 2.96264 11.9166 3.24996 11.9166H9.74996C10.0373 11.9166 10.3128 11.8024 10.516 11.5993C10.7192 11.3961 10.8333 11.1206 10.8333 10.8333V3.79159L8.12496 1.08325Z" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.58337 1.08325V3.24992C7.58337 3.53724 7.69751 3.81279 7.90068 4.01595C8.10384 4.21912 8.37939 4.33325 8.66671 4.33325H10.8334" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.41671 4.875H4.33337" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.66671 7.04175H4.33337" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.66671 9.20825H4.33337" stroke={active ? '#6941C6' : '#98A2B3'} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="art"><rect width="13" height="13" fill="white" /></clipPath></defs>
  </svg>
);

const IndieChatRightPanel: React.FC<Props> = ({
  activeTab,
  onTabChange,
  actionCards = defaultActionCards,
  weekLabel = 'This week',
  onActionSelect,
  onRefresh,
  refreshing = false,
  artifacts = [],
  artifactsLoading = false,
  onArtifactUpdate,
  onArtifactDownload,
  onArtifactDelete,
  onArtifactSelect,
  clientId,
  conversationId,
}) => {
  const tipContext = GlobalTip.useMessage();
  const [previewArtifact, setPreviewArtifact] = React.useState<OrbitArtifact | null>(null);
  const previewFile = useLiveGeneratedFile(previewArtifact?.file, previewArtifact?.file?.client_id ?? clientId);
  const fileClientId = (artifact: OrbitArtifact) => artifact.file?.client_id ?? clientId;

  const handleDownload = React.useCallback(async (artifact: OrbitArtifact) => {
    if (!artifact.file) {
      onArtifactDownload?.(artifact);
      return;
    }
    try {
      await downloadGeneratedFile(artifact.file.client_id ?? clientId, artifact.file);
    } catch {
      tipContext.show('Unable to download this file right now.');
    }
  }, [clientId, onArtifactDownload, tipContext]);
  const [isEditingPreview, setIsEditingPreview] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState('');
  const [draftContent, setDraftContent] = React.useState('');
  const [savingPreview, setSavingPreview] = React.useState(false);

  React.useEffect(() => {
    if (!previewArtifact) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewArtifact(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewArtifact]);

  React.useEffect(() => {
    if (!previewArtifact) return;
    const latest = artifacts.find((artifact) => artifact.id === previewArtifact.id);
    if (latest) {
      setPreviewArtifact(latest);
      if (!isEditingPreview) {
        setDraftTitle(latest.title);
        setDraftContent(latest.content_markdown);
      }
    }
  }, [artifacts, isEditingPreview, previewArtifact]);

  const handleArtifactOpen = React.useCallback((artifact: OrbitArtifact) => {
    setPreviewArtifact(artifact);
    setIsEditingPreview(false);
    setDraftTitle(artifact.title);
    setDraftContent(artifact.content_markdown);
    onArtifactSelect?.(artifact);
  }, [onArtifactSelect]);

  const handlePreviewClose = React.useCallback(() => {
    setPreviewArtifact(null);
    setIsEditingPreview(false);
    setDraftTitle('');
    setDraftContent('');
  }, []);

  const handleCopyArtifact = React.useCallback(async () => {
    if (!previewArtifact?.content_markdown) return;
    try {
      await navigator.clipboard.writeText(previewArtifact.content_markdown);
      tipContext.showSuccess('Artifact copied to clipboard');
    } catch {
      tipContext.show('Unable to copy the artifact right now.');
    }
  }, [previewArtifact, tipContext]);

  const handleShareArtifact = React.useCallback(async () => {
    if (!previewArtifact) return;
    const sharePayload = {
      title: previewArtifact.title,
      text: previewArtifact.content_markdown,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        tipContext.showSuccess('Artifact shared');
        return;
      }

      await navigator.clipboard.writeText(`${previewArtifact.title}\n\n${previewArtifact.content_markdown}`.trim());
      tipContext.showSuccess('Share text copied to clipboard');
    } catch (error) {
      const shareError = error as { name?: string } | undefined;
      if (shareError?.name === 'AbortError') {
        return;
      }
      tipContext.show('Unable to share the artifact right now.');
    }
  }, [previewArtifact, tipContext]);

  const handleEditToggle = React.useCallback(() => {
    if (!previewArtifact) return;
    setDraftTitle(previewArtifact.title);
    setDraftContent(previewArtifact.content_markdown);
    setIsEditingPreview(true);
  }, [previewArtifact]);

  const handleEditCancel = React.useCallback(() => {
    if (previewArtifact) {
      setDraftTitle(previewArtifact.title);
      setDraftContent(previewArtifact.content_markdown);
    }
    setIsEditingPreview(false);
  }, [previewArtifact]);

  const handleEditSave = React.useCallback(async () => {
    if (!previewArtifact || !onArtifactUpdate) return;
    setSavingPreview(true);
    try {
      const updated = await onArtifactUpdate(previewArtifact.id, {
        title: draftTitle,
        content_markdown: draftContent,
      });

      if (updated) {
        setPreviewArtifact(updated);
        setDraftTitle(updated.title);
        setDraftContent(updated.content_markdown);
      }
      setIsEditingPreview(false);
      tipContext.showSuccess('Artifact updated');
    } catch {
      tipContext.show('Unable to update the artifact right now.');
    } finally {
      setSavingPreview(false);
    }
  }, [draftContent, draftTitle, onArtifactUpdate, previewArtifact, tipContext]);

  return (
    <>
      <div className="indie-chat flex flex-col w-[220px] flex-shrink-0 h-full border-l border-[#EAECF0] bg-white overflow-hidden">

        {/* ── Tabs ── */}
        <div className="flex h-[37px] shrink-0 overflow-x-auto border-b border-[#EAECF0] px-2.5 [scrollbar-width:none]">
          {/* Actions */}
          <button
            onClick={() => onTabChange('actions')}
            className={`flex items-center justify-center gap-1 py-2 px-2 text-xs font-medium border-b-2 transition-colors ${activeTab === 'actions'
                ? 'text-[#101828] border-[#101828]'
                : 'text-[#98A2B3] border-transparent hover:text-[#667085]'
              }`}
          >
            <ActionsTabIcon active={activeTab === 'actions'} />
            <span>Actions</span>
          </button>

          {/* Artifacts */}
          <button
            onClick={() => onTabChange('artifacts')}
            className={`flex items-center justify-center gap-1 py-2 px-2 text-xs font-medium border-b-2 transition-colors ${activeTab === 'artifacts'
                ? 'text-[#101828] border-[#101828]'
                : 'text-[#98A2B3] border-transparent hover:text-[#667085]'
              }`}
          >
            <ArtifactsTabIcon active={activeTab === 'artifacts'} />
            <span>Artifacts</span>
            <span className="flex items-center justify-center w-4 h-4 rounded-[6px] bg-[#7F56D9] text-white text-[9px] font-bold leading-[13.5px] tracking-[0.167px]">
              {artifacts.length}
            </span>
          </button>

          {/* Files */}
          <button
            onClick={() => onTabChange('files')}
            className={`flex items-center justify-center gap-1 py-2 px-2 text-xs font-medium border-b-2 transition-colors ${activeTab === 'files'
                ? 'text-[#101828] border-[#101828]'
                : 'text-[#98A2B3] border-transparent hover:text-[#667085]'
              }`}
          >
            <FileSpreadsheet className={`w-[13px] h-[13px] ${activeTab === 'files' ? 'text-[#6941C6]' : 'text-[#98A2B3]'}`} />
            <span>Files</span>
          </button>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-[10px] pt-3 flex flex-col">

          {/* ── Actions tab ── */}
          {activeTab === 'actions' && (
            <div className="flex flex-1 flex-col items-center justify-start px-4 pt-16 text-center">
              <h3 className="text-[14px] font-semibold leading-[20px] text-[#667085] text-center">
                Action Items - Coming soon
              </h3>
              <p className="mt-1 text-[12px] font-normal leading-[16px] tracking-[0.12px] text-[#667085] text-center">
                You'll be notified when the feature is ready!
              </p>
            </div>
          )}

          {activeTab === 'files' && (
            <IndieGeneratedFilesTab clientId={clientId} conversationId={conversationId} />
          )}

          {/* ── Artifacts tab ── */}
          {activeTab === 'artifacts' && (
            artifactsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-[#98A2B3]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[11px]">Loading artifacts…</span>
              </div>
            ) : artifacts.length === 0 ? (
              <div className="flex flex-col items-center gap-1 text-center py-8 px-2">
                <FileText className="w-5 h-5 text-[#D0D5DD] mb-1" />
                <p className="text-[#98A2B3] text-[11px] font-medium">No saved artifacts yet</p>
                <p className="text-[#C0C6D1] text-[10px] leading-[14px]">
                  Save an Indie response as an artifact to keep it here for later.
                </p>
              </div>
            ) : (
              Object.entries(artifacts.reduce<Record<string, OrbitArtifact[]>>((groups, artifact) => {
                const key = artifactSectionLabel(artifact.created_at);
                groups[key] = [...(groups[key] ?? []), artifact];
                return groups;
              }, {})).map(([section, items]) => (
                <div key={section} className="mb-4 last:mb-0">
                  <span className="text-[#98A2B3] text-[9px] font-semibold leading-[13.5px] tracking-[0.977px] uppercase block mb-[9px] px-[6px]">
                    {section}
                  </span>
                  <div className="flex flex-col gap-[4px]">
                    {items.map((artifact) => {
                      const visual = artifactVisual(artifact);
                      const rowProps = {
                        iconBg: visual.iconBg,
                        icon: visual.icon,
                        title: artifact.title,
                        sub: artifact.preview_text || artifactSubtitle(artifact),
                        onClick: () => handleArtifactOpen(artifact),
                        onDownload: () => void handleDownload(artifact),
                        onDelete: () => onArtifactDelete?.(artifact),
                      };
                      return artifact.file
                        ? <FileArtifactRow key={artifact.id} {...rowProps} file={artifact.file} clientId={fileClientId(artifact)} />
                        : <ArtifactRow key={artifact.id} {...rowProps} />;
                    })}
                  </div>
                </div>
              ))
            )
          )}
        </div>
      </div>

      {previewArtifact && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/40 p-4" onClick={handlePreviewClose}>
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-[20px] border border-[#EAECF0] bg-white shadow-2xl flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[#EAECF0] px-5 py-4">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-[#101828] truncate">{previewArtifact.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[#667085]">
                  <span className="inline-flex items-center rounded-full bg-[#F9F5FF] px-2 py-0.5 font-medium capitalize text-[#6941C6]">
                    {previewArtifact.artifact_type.replace(/_/g, ' ')}
                  </span>
                  <span>{artifactSubtitle(previewArtifact)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isEditingPreview ? (
                  <>
                    {!previewArtifact.file && <button
                      type="button"
                      onClick={handleEditToggle}
                      className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>}
                    <button
                      type="button"
                      onClick={() => void handleCopyArtifact()}
                      className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleShareArtifact()}
                      className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      Share
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleEditCancel}
                      disabled={savingPreview}
                      className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB] disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleEditSave()}
                      disabled={savingPreview}
                      className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#7F56D9] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-[#6941C6] disabled:opacity-50"
                    >
                      <Check className="h-3.5 w-3.5" />
                      {savingPreview ? 'Saving…' : 'Save'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => void handleDownload(previewArtifact)}
                  disabled={savingPreview || (previewFile ? previewFile.status !== 'ready' : false)}
                  className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB] disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={handlePreviewClose}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#98A2B3] hover:bg-[#F9FAFB] hover:text-[#667085]"
                  aria-label="Close artifact preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 bg-[#FCFCFD]">
              {previewFile ? (
                isBuilding(previewFile.status) ? (
                  <div className="flex items-center gap-2 py-6 text-[12px] text-[#6941C6]" role="status">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Building {FILE_FORMAT_LABEL[previewFile.file_format] ?? previewFile.file_format}… about a minute
                  </div>
                ) : previewFile.status === 'error' ? (
                  <p className="py-6 text-[12px] text-[#B42318]">{previewFile.error_message || 'The file could not be built.'}</p>
                ) : fileClientId(previewArtifact) ? (
                  <GeneratedFilePreview clientId={fileClientId(previewArtifact) as number} file={previewFile} />
                ) : null
              ) : isEditingPreview ? (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-[11px] font-medium text-[#344054] mb-1.5">Title</label>
                    <input
                      type="text"
                      value={draftTitle}
                      onChange={(event) => setDraftTitle(event.target.value)}
                      maxLength={300}
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 py-2 text-[13px] text-[#101828] outline-none focus:border-[#7F56D9]"
                      placeholder="Artifact title"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#344054] mb-1.5">Markdown</label>
                    <textarea
                      value={draftContent}
                      onChange={(event) => setDraftContent(event.target.value)}
                      className="min-h-[360px] w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 py-2 text-[13px] text-[#101828] outline-none focus:border-[#7F56D9]"
                      placeholder="Artifact markdown"
                    />
                  </div>
                </div>
              ) : (
                <div className="indie-markdown max-w-none text-[#101828]">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {previewArtifact.content_markdown || previewArtifact.preview_text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Artifact row backed by a generated file: shows its format and live build status. */
const FileArtifactRow: React.FC<ArtifactRowProps & { file: ArtifactFile; clientId?: number | null }> = ({ file, clientId, ...rest }) => {
  const live = useLiveGeneratedFile(file, clientId) ?? file;
  return <ArtifactRow {...rest} sub={fileStatusLabel(live)} />;
};

// ─── Artifact row helper ─────────────────────────────────────────────────────

interface ArtifactRowProps {
  iconBg: string;
  icon: React.ReactNode;
  title: string;
  sub: string;
  onClick?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}
const ArtifactRow: React.FC<ArtifactRowProps> = ({ iconBg, icon, title, sub, onClick, onDownload, onDelete }) => (
  <div className="flex items-center gap-[9px] p-[9px_10px] rounded-[10px] border border-[#EAECF0] bg-[#F9FAFB] hover:border-[#DDD6FE] hover:bg-[#F4EBFF] transition-colors">
    <div
      className="flex-shrink-0 w-7 h-7 rounded-[7px] flex items-center justify-center"
      style={{ background: iconBg }}
    >
      {icon}
    </div>
    <button type="button" onClick={onClick} className="flex-1 min-w-0 text-left">
      <p className="text-[#101828] text-[11px] font-medium leading-[16.5px] tracking-[0.064px] truncate">{title}</p>
      <p className="text-[#98A2B3] text-[10px] font-normal leading-[15px] tracking-[0.117px] line-clamp-2">{sub}</p>
    </button>
    <div className="flex items-center gap-[2px] flex-shrink-0">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDownload?.();
        }}
        className="w-[22px] h-[22px] flex items-center justify-center rounded-[5px] hover:bg-[#F9FAFB]"
      >
        <Download className="w-3 h-3 text-[#98A2B3]" />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDelete?.();
        }}
        className="w-[22px] h-[22px] flex items-center justify-center rounded-[5px] hover:bg-[#FEE4E2]"
      >
        {onDelete ? <Trash2 className="w-3 h-3 text-[#D92D20]" /> : <MoreVertical className="w-3 h-3 text-[#98A2B3]" />}
      </button>
    </div>
  </div>
);

export default IndieChatRightPanel;